import React from 'react';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import { toSentenceCase } from '@noths/polaris-client-utils';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import { RecommendedProducts } from 'src/components/organisms/RecommendedProducts/RecommendedProducts';
import * as styles from './ProductCarouselRecommendationsSection.style';

const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

export interface ProductCarouselRecommendationsSectionProps {
  currency: CurrencyCode;
  servingConfig: RecommendationsServingConfig;
  trackingPosition: number;
  trackingTitle: string;
}

export const ProductCarouselRecommendationsSection = ({
  currency,
  servingConfig,
  trackingPosition,
  trackingTitle,
}: ProductCarouselRecommendationsSectionProps) => {
  /* Derived once here rather than in `RecommendedProducts`, so the placeholder
     and the carousel it becomes are named the same way from one source, and the
     heading is in the server-rendered HTML rather than appearing with the
     products. */
  const heading = toSentenceCase(trackingTitle);

  return (
    <SectionContainer columnsXL={10} maxWidth="max" wrapperStyles={styles.wrapper}>
      {/* `LazyRender` holds the client fetch back until the carousel is near the
          viewport. Its placeholder is the carousel's own loading state rather
          than a separate skeleton, so nothing about the rail changes shape when
          the real one mounts. */}
      <LazyRender
        options={{
          rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
        }}
        placeholder={<ProductCarousel heading={heading} products={null} />}
      >
        <RecommendedProducts
          currency={currency}
          heading={heading}
          servingConfig={servingConfig}
          trackingPosition={trackingPosition}
          trackingTitle={trackingTitle}
        />
      </LazyRender>
    </SectionContainer>
  );
};
